import React, { useState, useEffect } from 'react';
import api from '../../api/auth';

const AdminAbsences = () => {
  const [absences, setAbsences] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  const [filterClass, setFilterClass] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => { fetchClasses(); fetchAbsences(); }, []);
  useEffect(() => { fetchAbsences(); }, [filterClass, filterType, filterStatus]);

  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/classes', { headers: { Authorization: `Bearer ${token}` } });
      setClasses(Array.isArray(res.data) ? res.data : []);
    } catch (err) { console.error(err); }
  };

  const fetchAbsences = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterClass) params.append('class_id', filterClass);
      if (filterType) params.append('type', filterType);
      if (filterStatus) params.append('status', filterStatus);
      const res = await api.get(`/admin/absences?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      setAbsences(Array.isArray(res.data) ? res.data : []);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleApprove = async (id) => {
    if (!window.confirm('Approuver cette justification ?')) return;
    try {
      setProcessing(id);
      const token = localStorage.getItem('token');
      await api.post(`/teacher/absences/${id}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      alert('✅ Justification approuvée');
      fetchAbsences();
    } catch (err) { alert('❌ Erreur : ' + (err.response?.data?.error || err.message)); }
    finally { setProcessing(null); }
  };

  const handleReject = async (id) => {
    const reason = window.prompt('Motif du refus :', '');
    if (reason === null) return;
    try {
      setProcessing(id);
      const token = localStorage.getItem('token');
      await api.post(`/teacher/absences/${id}/reject`, { reason }, { headers: { Authorization: `Bearer ${token}` } });
      alert('❌ Justification refusée');
      fetchAbsences();
    } catch (err) { alert('❌ Erreur : ' + (err.response?.data?.error || err.message)); }
    finally { setProcessing(null); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette absence ?')) return;
    try {
      setProcessing(id);
      const token = localStorage.getItem('token');
      await api.delete(`/admin/absences/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      alert('🗑️ Absence supprimée');
      fetchAbsences();
    } catch (err) { alert('❌ Erreur : ' + (err.response?.data?.error || err.message)); }
    finally { setProcessing(null); }
  };

  const getStatusBadge = (abs) => {
    if (abs.is_justified) return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">✅ Justifiée</span>;
    if (abs.justification_status === 'pending') return <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">⏳ En attente</span>;
    if (abs.justification_status === 'rejected') return <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">❌ Refusée</span>;
    return <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">🔴 Non justifiée</span>;
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'absence':
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">🚨 Absence</span>;
      case 'retard':
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">⏰ Retard</span>;
      case 'renvoye':
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">🚪 Renvoyé</span>;
      default:
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{type}</span>;
    }
  };

  const pendingCount = absences.filter(a => a.justification_status === 'pending').length;
  const renvoyeCount = absences.filter(a => a.type === 'renvoye').length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#2C3E50]">🚨 Gestion des absences</h1>
        <p className="text-gray-500">Consultez et validez les justifications</p>
      </div>

      {pendingCount > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
          <p className="font-bold text-orange-800">📨 {pendingCount} demande(s) de justification en attente</p>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-blue-500">
          <p className="text-sm text-gray-500">Total</p>
          <p className="text-2xl font-bold text-blue-600">{absences.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-purple-500">
          <p className="text-sm text-gray-500">Renvoyés</p>
          <p className="text-2xl font-bold text-purple-600">{renvoyeCount}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-green-500">
          <p className="text-sm text-gray-500">Justifiées</p>
          <p className="text-2xl font-bold text-green-600">{absences.filter(a => a.is_justified).length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-orange-500">
          <p className="text-sm text-gray-500">En attente</p>
          <p className="text-2xl font-bold text-orange-600">{pendingCount}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-red-500">
          <p className="text-sm text-gray-500">Non justifiées</p>
          <p className="text-2xl font-bold text-red-600">{absences.filter(a => !a.is_justified && a.justification_status !== 'pending').length}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <select value={filterClass} onChange={(e) => setFilterClass(e.target.value)} className="px-3 py-2 border rounded-lg text-sm">
            <option value="">Toutes les classes</option>
            {classes.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
          </select>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="px-3 py-2 border rounded-lg text-sm">
            <option value="">Tous les types</option>
            <option value="absence">🚨 Absence</option>
            <option value="retard">⏰ Retard</option>
            <option value="renvoye">🚪 Renvoyé</option>
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-2 border rounded-lg text-sm">
            <option value="">Tous les statuts</option>
            <option value="justified">✅ Justifiées</option>
            <option value="not_justified">🔴 Non justifiées</option>
          </select>
          <button onClick={() => { setFilterClass(''); setFilterType(''); setFilterStatus(''); }} className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300">🔄 Réinitialiser</button>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl shadow-md p-8 text-center text-gray-500">Chargement...</div>
      ) : absences.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-8 text-center text-gray-500">
          <p className="text-lg mb-2">🎉 Aucune absence trouvée</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Élève</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Classe</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Date / Heure</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Type</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Statut</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Motif</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {absences.map((abs) => (
                  <tr key={abs.id} className="border-t hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">{abs.student_name}</td>
                    <td className="py-3 px-4 text-sm">{abs.class_name}</td>
                    <td className="py-3 px-4 text-sm">
                      {abs.date} {abs.time && <span className="text-gray-400 text-xs ml-1">à {abs.time}</span>}
                    </td>
                    <td className="py-3 px-4">{getTypeBadge(abs.type)}</td>
                    <td className="py-3 px-4">{getStatusBadge(abs)}</td>
                    <td className="py-3 px-4 text-sm text-gray-500">{abs.reason || '-'}</td>
                    <td className="py-3 px-4">
                      <div className="flex space-x-2 flex-wrap">
                        {abs.justification_status === 'pending' && (
                          <>
                            <button onClick={() => handleApprove(abs.id)} disabled={processing === abs.id} className="px-3 py-1 bg-green-500 text-white rounded-lg text-xs hover:bg-green-600 disabled:opacity-50">✅</button>
                            <button onClick={() => handleReject(abs.id)} disabled={processing === abs.id} className="px-3 py-1 bg-red-500 text-white rounded-lg text-xs hover:bg-red-600 disabled:opacity-50">❌</button>
                          </>
                        )}
                        <button onClick={() => handleDelete(abs.id)} disabled={processing === abs.id} className="px-3 py-1 bg-gray-500 text-white rounded-lg text-xs hover:bg-gray-600 disabled:opacity-50">🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAbsences;